//@ts-strict-ignore
import {
  AfterViewInit,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { SohoTabsComponent } from 'ids-enterprise-ng';
import { LMBaseComponent, LMDialogInfo, LMDialogType } from '@base/lm-base.component';
import { LMSettingsToolbarService } from './lm-settings-toolbar.service';
import { LMBannerHeaderComponentRef } from '@banner/lm-banner-header-component-ref.service';
import { LMContextService } from '@services/lm-context.service';
import { LMBusyIndicatorService, LMComponentInfoService, LMLoggerService, LMRouteComponentService, LMModalOpenService, LMModalWrapperService } from '@app/services';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { LMToolbarComponent } from '@toolbar/lm-toolbar.component';
import { LMDialogButtonKey, LMGlobalUtilService, LMUniqueIdService } from '@app/utils';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import { LMApplicationMenuSettingsDialogComponent, SaveRequired } from '@app/components';
import { LMComponentTrackerService } from '@services/lm-component-tracker.service';

@Component({
  selector:    'lm-settings',
  standalone: false,
  templateUrl: 'lm-settings.component.html',
  styleUrls:   [ 'lm-settings.component.scss' ],
  providers:   [
  LMComponentTrackerService
  ],
  })
export class LMSettingsComponent extends LMBaseComponent implements AfterViewInit, OnInit, OnDestroy, SaveRequired {

  @ViewChild(LMApplicationMenuSettingsDialogComponent) lmApplicationMenuSettingsDialogComponent: LMApplicationMenuSettingsDialogComponent;

  public toolbarButtons: Observable<Array<LMToolbarButton>>;
  private toolbarActionRefArray: Array<LMBusActionReference> = [];
  private subscriptionsArray: Array<Subscription> = [];
  public pageTitle = Soho.Locale.translate('UserSettings');
  private screenId = Soho.Locale.translate('UserSettings');
  private dirtyCheckComplete = false;

  @ViewChild(LMToolbarComponent) toolbar: LMToolbarComponent;
  @ViewChild(SohoTabsComponent) tabs: SohoTabsComponent;

  @HostBinding('class.form-layout-compact') @Input() useCompactFormLayout: boolean;
  @HostBinding('class.lm-display-type-is-smartphone') displayTypeIsSmartPhone: boolean;

  constructor(
    protected router: Router,
    public    route: ActivatedRoute,
    protected lmBannerHeaderCompRef: LMBannerHeaderComponentRef,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmContextService: LMContextService,
    protected lmGlobalUtilService: LMGlobalUtilService,
    protected lmLoggerService: LMLoggerService,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmSettingsToolbarService: LMSettingsToolbarService,
    protected lmAppComponentService: LMAppComponentService,
    protected lmComponentTrackerService: LMComponentTrackerService,
    protected lmRouteComponentService: LMRouteComponentService,
    protected lmModalWrapperService: LMModalWrapperService,
    protected lmModalOpenService: LMModalOpenService
  ) {
    super (lmBusyIndicatorService, lmComponentInfoService, lmUniqueIdService, lmAppComponentService, lmModalWrapperService, lmModalOpenService);
  }

  ngOnInit() {
    this.lmRouteComponentService.setRouteComponent(this as Component);
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  ngAfterViewInit() {
    this.toolbarActionRefArray = [ {
      id: 'settings-save',
      automationId: 'settings-save',
      icon: 'save',
      label: Soho.Locale.translate('Save'),
      type: 'standard',
    } ];

    this.buildToolBar();
  }

  ngOnDestroy() {
    this.subscriptionsArray.forEach(subscription => {
      subscription.unsubscribe();
    });
    if (this.lmContextService.isTrackerEnabled()) {
      this.lmComponentTrackerService.endComponentTracking();
    }
    setTimeout(() => {
      this.lmBannerHeaderCompRef = undefined;
    });
  }

  private buildToolBar() {
    if (this.lmContextService.inMingleWidget()) {
      return;
    }
    this.lmBannerHeaderCompRef.instance.toolbarButtons = this.lmSettingsToolbarService.getToolbarButtonsObservable();

    setTimeout(() => {
      // need to place in a timeout so the toolbarService has a change to set its observable member.
      this.lmBannerHeaderCompRef.instance.screenId = this.screenId;
      this.lmBannerHeaderCompRef.instance.screenAutomationId = this.screenId;
      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = this.pageTitle;
      this.lmSettingsToolbarService.buildToolbar(this.toolbarActionRefArray);
      this.subscriptionsArray.push(this.lmBannerHeaderCompRef.instance.toolbarButtonEvent.subscribe((e) => {
        if (e.actionReference?.id === 'settings-save') {
          this.runSave();
        } else if (e.actionReference?.id === 'settings-cancel') {
          this.runCancel();
        }
      }));
    }, 1);
  }

  getComponentInfo(): LMComponentInfo {
    return {
      componentType: 'USER_SETTINGS',
      name: 'UserSettings',
      userWebRequestId: this.getUserWebRequestId(),
      type: 'page',
      developer: false,
      isWizard: false,
      panelNavigation: false,
      hasConfigAccess: false,
    };
  }

  getComponentPlaceHolder(): ViewContainerRef {
    return undefined;
  }

  public getUserWebRequestId(): string {
    return 'UserSettings' + '-' + this.getBaseCompId();
  }

  public runSave() {
    this.lmApplicationMenuSettingsDialogComponent.saveSettings();

    const title = '';
    const message = 'User Settings Saved';
    this.lmGlobalUtilService.displayToastMessage(title, message);
    this.lmSettingsToolbarService.updateToolbarState(true);
  }

  private runCancel() {
    this.lmApplicationMenuSettingsDialogComponent.cancelSettings();

    const title = '';
    const message = 'User Settings Canceled';
    this.lmGlobalUtilService.displayToastMessage(title, message);
  }

  public allowPageNavigation(nextState?: RouterStateSnapshot, fromUnifiedMenu?: boolean): Promise<boolean> {
    // Skip angular route guard dirty check if it was already done by the unified Portal menu.
    if (this.dirtyCheckComplete) {
      this.dirtyCheckComplete = false;
      return Promise.resolve(true);
    }

    const hasUnsavedChanges = this.checkSaveRequired(fromUnifiedMenu);

    if (!hasUnsavedChanges) {
      this.dirtyCheckComplete = true;
      this.closeModals();
      return Promise.resolve(true);
    }

    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const info: LMDialogInfo = { message: Soho.Locale.translate('NavigateAwayMessage'), type: LMDialogType.LEAVESTAY };

    this.showUnsavedChanges(info).subscribe((response: UnsavedChangesResponse) => {
      if (response === LMDialogButtonKey.LEAVE) {
        this.dirtyCheckComplete = true;
        this.lmApplicationMenuSettingsDialogComponent.isDirty = false;
        this.closeModals();
        youMayNavigateSource.next(true);
        youMayNavigateSource.complete();
      } else {
        // Cancel. Stay put!
        youMayNavigateSource.next(false);
        youMayNavigateSource.complete();
      }
    });
    return lastValueFrom(youMayNavigate);
  }

  checkSaveRequired(fromUnifiedMenu?: boolean): boolean {
    return (this.isModified() || this.modalNavigationBlocked(fromUnifiedMenu));
  }

  private isModified() {
    return this.lmApplicationMenuSettingsDialogComponent.isModified();
  }

  saveComponentState(includeSubComponents?: boolean): void {
  }
}
